import useApi from "@/hooks/useApi";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Trigger, WorkflowListItem, triggerDefs } from "./types";
import { Checkbox, WorkflowItem } from "./WorkflowHelpers";
import useWorkflows from "@/hooks/useWorkflows";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useActivityLog from "@/hooks/useActivityLog";
import useTrackEvent from "@/hooks/useTrackEvent";
import { useNavigate } from "react-router-dom";
import BulkActionBar from "@/components/BulkActionBar";
import WorkflowsJourneyView from "./WorkflowsJourneyView";

type StatusFilter = "active" | "all" | "archived";
type GroupBy = "trigger" | "category" | "flat";

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "archived", label: "Archived" },
];

const GROUP_BY_STORAGE_KEY = "workflows.groupBy";

/**
 * Triggers where "priority" is meaningful (multiple workflows compete,
 * first-match-by-priority wins). Everything else just fires on the event
 * and priority is not a useful sort key.
 */
const PRIORITY_RELEVANT_TRIGGERS = new Set<string>([
  "conversation_opened",
  "widget_loaded",
  "chat_button_clicked",
]);

function isPriorityRelevant(triggerType: string): boolean {
  return PRIORITY_RELEVANT_TRIGGERS.has(triggerType);
}

function TriggerBadge({ trigger }: { trigger: Trigger }) {
  const label = triggerDefs[trigger] ?? trigger;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
      {label}
    </span>
  );
}

function RunModeBadge({ runMode }: { runMode?: string }) {
  if (!runMode || runMode === "exclusive") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        Exclusive
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
      Always
    </span>
  );
}

export default function WorkflowsLandingV2() {
  const { workflows, setWorkflows, fetch } = useWorkflows();
  const api = useApi();
  const { track } = useActivityLog();
  const { track: trackEvent } = useTrackEvent();
  const { displayToast } = useToastAlert();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [reusableOpen, setReusableOpen] = useState(false);
  const [recentlyHiddenIds, setRecentlyHiddenIds] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<"list" | "journey">("list");
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    try {
      const v = localStorage.getItem(GROUP_BY_STORAGE_KEY);
      if (v === "trigger" || v === "category" || v === "flat") return v;
    } catch { /* ignore */ }
    return "trigger";
  });
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    try { localStorage.setItem(GROUP_BY_STORAGE_KEY, groupBy); } catch { /* ignore */ }
  }, [groupBy]);

  useEffect(() => {
    fetch();
    trackEvent("feature_click", "Workflows/V2Landing/open");
  }, [fetch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, groupBy]);

  const templates = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => w.isTemplate) : []),
    [workflows]
  );
  const regularWorkflows = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => !w.isTemplate) : []),
    [workflows]
  );

  // Workflows passing status filter (before category pill narrowing). Used to
  // compute the category chip row so chips don't vanish when you click them.
  const statusFilteredWorkflows = useMemo(() => {
    return regularWorkflows.filter((w) => {
      const isArchived = w.workflowStatus === "deprecated";
      if (statusFilter === "active" && (isArchived || !w.enabled)) return false;
      if (statusFilter === "archived" && !isArchived) return false;
      return true;
    });
  }, [regularWorkflows, statusFilter]);

  const visibleWorkflows = useMemo(() => {
    if (activeCategories.size === 0) return statusFilteredWorkflows;
    return statusFilteredWorkflows.filter((w) => {
      const cat = (w.category && w.category.trim()) || "Uncategorized";
      return activeCategories.has(cat);
    });
  }, [statusFilteredWorkflows, activeCategories]);

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    statusFilteredWorkflows.forEach((w) => {
      const cat = (w.category && w.category.trim()) || "Uncategorized";
      set.add(cat);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [statusFilteredWorkflows]);

  function toggleCategory(cat: string, additive: boolean) {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (additive) {
        if (next.has(cat)) next.delete(cat); else next.add(cat);
      } else {
        if (next.size === 1 && next.has(cat)) {
          next.clear();
        } else {
          next.clear();
          next.add(cat);
        }
      }
      return next;
    });
  }

  async function toggleWorkflow(id: number) {
    if (!workflows) return;
    const index = workflows.findIndex((w) => w.id === id);
    if (index === -1) return;
    const workflow = workflows[index];
    const wasEnabled = workflow.enabled;
    const clone = [...workflows];
    clone[index] = { ...workflow, enabled: !wasEnabled };
    setWorkflows(clone);
    if (wasEnabled) {
      setRecentlyHiddenIds((prev) => new Set([...prev, id]));
    } else {
      setRecentlyHiddenIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
    try {
      await api.messaging.post(`Workflows/toggle/${id}`);
    } catch {
      const reverted = [...clone];
      reverted[index] = { ...workflow, enabled: wasEnabled };
      setWorkflows(reverted);
      displayToast(Severity.Error, "Failed to update workflow status");
      return;
    }
    track({
      category: "Workflows",
      action: wasEnabled ? "WorkflowDisabled" : "WorkflowEnabled",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow.name
    });
  }

  async function removeWorkflow(id: number) {
    if (workflows === null) return;
    const workflow = workflows.find((w) => w.id === id);
    try {
      const resp = await api.messaging.delete(`Workflows/${id}`);
      if (resp.status === 409) {
        const body = await resp.json().catch(() => ({}));
        displayToast(Severity.Error, body?.error ?? "Cannot delete this template. Detach linked workflows first.");
        return;
      }
    } catch {
      displayToast(Severity.Error, "Failed to delete workflow");
      return;
    }
    setWorkflows(workflows?.filter((w) => w.id !== id));
    track({
      category: "Workflows",
      action: "WorkflowDeleted",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow?.name
    });
  }

  async function duplicateWorkflow(id: number) {
    try {
      const resp = await api.messaging.post(`Workflows/${id}/duplicate`, {});
      if (!resp.ok) { displayToast(Severity.Error, "Failed to duplicate workflow"); return; }
      const body = await resp.json();
      await fetch();
      displayToast(Severity.Success, `Workflow duplicated: "${body?.name ?? "copy"}" (disabled — enable when ready)`);
    } catch {
      displayToast(Severity.Error, "Failed to duplicate workflow");
    }
  }

  async function archiveWorkflow(id: number, replacedById?: number) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/status`, { status: "deprecated", replacedById: replacedById ?? null });
      if (!resp.ok) { displayToast(Severity.Error, "Failed to archive workflow"); return; }
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, workflowStatus: "deprecated", replacedById: replacedById ?? null, enabled: false } : w));
      if (statusFilter === "active") setRecentlyHiddenIds((prev) => new Set([...prev, id]));
      displayToast(Severity.Success, "Workflow archived");
    } catch {
      displayToast(Severity.Error, "Failed to archive workflow");
    }
  }

  async function unarchiveWorkflow(id: number) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/status`, { status: "active", replacedById: null });
      if (!resp.ok) { displayToast(Severity.Error, "Failed to unarchive workflow"); return; }
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, workflowStatus: null, replacedById: null, enabled: false } : w));
      displayToast(Severity.Success, "Workflow unarchived (disabled — enable when ready)");
    } catch {
      displayToast(Severity.Error, "Failed to unarchive workflow");
    }
  }

  async function detachWorkflow(id: number) {
    try {
      await api.messaging.post(`Workflows/detach/${id}`);
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, templateId: null, templateName: null } : w));
      displayToast(Severity.Success, "Workflow detached. You can now edit it independently.");
    } catch {
      displayToast(Severity.Error, "Failed to detach workflow from template");
    }
  }

  async function renameWorkflow(id: number, newName: string) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/name`, { name: newName });
      if (!resp.ok) { displayToast(Severity.Error, "Failed to rename workflow"); return; }
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, name: newName } : w));
    } catch {
      displayToast(Severity.Error, "Failed to rename workflow");
    }
  }

  async function bulkArchive() {
    const ids = [...selectedIds];
    await Promise.all(ids.map((id) => api.messaging.patch(`Workflows/${id}/status`, { status: "deprecated", replacedById: null })));
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, workflowStatus: "deprecated", enabled: false } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} archived.`);
  }

  async function bulkEnable() {
    const ids = [...selectedIds];
    for (const id of ids) await api.messaging.post(`Workflows/toggle/${id}`);
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, enabled: true } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} enabled.`);
  }

  async function bulkDisable() {
    const ids = [...selectedIds];
    for (const id of ids) await api.messaging.post(`Workflows/toggle/${id}`);
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, enabled: false } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} disabled.`);
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll(ids: number[]) {
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  function renderPriorityBanner() {
    return (
      <div className="mx-4 mt-3 mb-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-semibold">Execution order:</span>{" "}
        Priority Rules always fire first → then Workflows (first match by priority) → then Always-run Workflows → then Automation/AI Rules.
        If a routing Rule exists for the same conditions, it only fires if no Workflow matched.
      </div>
    );
  }

  function renderTopbar() {
    return (
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <h1 className="text-xl font-bold text-slate-800 flex-1">Workflows</h1>
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5" role="tablist" aria-label="View mode">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "list"}
            onClick={() => setViewMode("list")}
            className={`text-xs px-3 py-1 rounded-md transition-colors font-medium ${
              viewMode === "list" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "journey"}
            onClick={() => setViewMode("journey")}
            className={`text-xs px-3 py-1 rounded-md transition-colors font-medium ${
              viewMode === "journey" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Journey
          </button>
        </div>
        <button
          type="button"
          onClick={() => { trackEvent("feature_click", "Workflows/SettingsHub/open"); navigate("/settings/hub"); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50 transition-colors"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          Settings
        </button>
        <button
          type="button"
          onClick={() => navigate("/workflows/create")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create Workflow
        </button>
      </div>
    );
  }

  function renderFilterRow() {
    const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
      { value: "trigger", label: "By trigger" },
      { value: "category", label: "By category" },
      { value: "flat", label: "Flat" },
    ];
    return (
      <div className="px-4 pt-2 pb-2 border-b border-slate-200 flex flex-col gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status filter */}
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5 shrink-0">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={`text-xs px-3 py-1 rounded-md transition-colors font-medium ${
                  statusFilter === tab.value ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Group-by toggle */}
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-slate-500">Group:</span>
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
              {GROUP_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setGroupBy(o.value)}
                  className={`text-xs px-3 py-1 rounded-md transition-colors font-medium ${
                    groupBy === o.value ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1" />
          <span className="text-xs text-slate-400">
            {visibleWorkflows.length} workflow{visibleWorkflows.length === 1 ? "" : "s"}
          </span>
        </div>

        {/* Category chip row */}
        {availableCategories.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pt-1">
            <button
              type="button"
              onClick={() => setActiveCategories(new Set())}
              className={`shrink-0 px-3 py-1 text-xs rounded-full border transition-colors ${
                activeCategories.size === 0
                  ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              All
            </button>
            {availableCategories.map((cat) => {
              const active = activeCategories.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={(e) => toggleCategory(cat, e.metaKey || e.ctrlKey || e.shiftKey)}
                  title="Click to filter · Cmd/Ctrl-click to add multiple"
                  className={`shrink-0 px-3 py-1 text-xs rounded-full border transition-colors ${
                    active
                      ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderConditionLabel(w: WorkflowListItem) {
    if (w.conditionSummary) return <span className="text-xs text-slate-600 max-w-[200px] truncate">{w.conditionSummary}</span>;
    return <span className="text-xs text-slate-400 italic">Always</span>;
  }

  /**
   * Sort + priority-column relevance is per-section. If ANY workflow in the
   * section has a priority-relevant trigger, we show the Priority column for
   * that section. Rows whose trigger is not priority-relevant show "—".
   */
  function sortSection(items: WorkflowListItem[], sectionUsesPriority: boolean): WorkflowListItem[] {
    const copy = [...items];
    if (sectionUsesPriority) {
      copy.sort((a, b) => {
        const ap = isPriorityRelevant(a.triggerType);
        const bp = isPriorityRelevant(b.triggerType);
        if (ap && bp) {
          const apri = a.routingPriority ?? Number.MAX_SAFE_INTEGER;
          const bpri = b.routingPriority ?? Number.MAX_SAFE_INTEGER;
          if (apri !== bpri) return apri - bpri;
          return a.id - b.id;
        }
        if (ap) return -1;
        if (bp) return 1;
        return a.name.localeCompare(b.name);
      });
    } else {
      copy.sort((a, b) => a.name.localeCompare(b.name));
    }
    return copy;
  }

  function renderEmpty() {
    return (
      <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
        <span className="text-sm">No workflows in this view</span>
        <button
          type="button"
          onClick={() => navigate("/workflows/create")}
          className="mt-1 text-sm text-[var(--color-primary)] underline hover:opacity-80"
        >
          Create your first workflow
        </button>
      </div>
    );
  }

  function renderSectionTable(
    items: WorkflowListItem[],
    opts: { showPriorityColumn: boolean; keyPrefix: string }
  ) {
    const allIds = items.map((w) => w.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    return (
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 h-10 text-xs text-slate-500 font-semibold">
            <th className="w-10 pl-3">
              <Checkbox checked={allSelected} onChange={() => selectAll(allIds)} />
            </th>
            <th className="pl-2 w-1/4">Name</th>
            <th className="w-1/5">Runs when</th>
            <th className="w-1/6">Trigger</th>
            <th className="w-24">Mode</th>
            {opts.showPriorityColumn && <th className="w-20">Priority</th>}
            <th className="w-24">Status</th>
            <th className="w-16 text-center pr-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w, i) => (
            <tr
              key={`${opts.keyPrefix}-${w.id}`}
              className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i === items.length - 1 ? "border-b-0" : ""}`}
            >
              <td className="pl-3 py-3">
                <Checkbox checked={selectedIds.has(w.id)} onChange={() => toggleSelect(w.id)} />
              </td>
              <td className="pl-2 py-3">
                <button
                  type="button"
                  onClick={() => navigate(`/workflows/${w.id}`)}
                  className="text-sm font-medium text-slate-800 hover:text-[var(--color-primary)] transition-colors text-left"
                >
                  {w.name}
                </button>
                <div className="text-xs text-slate-400 mt-0.5">
                  {w.lastModified ? new Date(w.lastModified).toLocaleDateString() : ""}
                </div>
              </td>
              <td className="py-3">{renderConditionLabel(w)}</td>
              <td className="py-3"><TriggerBadge trigger={w.triggerType} /></td>
              <td className="py-3"><RunModeBadge runMode={w.runMode} /></td>
              {opts.showPriorityColumn && (
                <td className="py-3">
                  {isPriorityRelevant(w.triggerType) ? (
                    <span className="text-xs text-slate-500 font-mono">
                      {w.routingPriority != null ? w.routingPriority : "–"}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Runs on event</span>
                  )}
                </td>
              )}
              <td className="py-3">
                <WorkflowItem
                  item={w}
                  isLast={i === items.length - 1}
                  isSelected={selectedIds.has(w.id)}
                  onSelect={() => toggleSelect(w.id)}
                  toggle={() => toggleWorkflow(w.id)}
                  remove={() => removeWorkflow(w.id)}
                  duplicate={() => duplicateWorkflow(w.id)}
                  onDetach={w.templateId ? () => detachWorkflow(w.id) : undefined}
                  onArchive={w.workflowStatus !== "deprecated" ? (replacedById) => archiveWorkflow(w.id, replacedById) : undefined}
                  onUnarchive={w.workflowStatus === "deprecated" ? () => unarchiveWorkflow(w.id) : undefined}
                  onRename={renameWorkflow}
                  renderStatusToggleOnly
                />
              </td>
              <td className="py-3 pr-3 text-center">
                <WorkflowItem
                  item={w}
                  isLast={i === items.length - 1}
                  isSelected={selectedIds.has(w.id)}
                  onSelect={() => toggleSelect(w.id)}
                  toggle={() => toggleWorkflow(w.id)}
                  remove={() => removeWorkflow(w.id)}
                  duplicate={() => duplicateWorkflow(w.id)}
                  onDetach={w.templateId ? () => detachWorkflow(w.id) : undefined}
                  onArchive={w.workflowStatus !== "deprecated" ? (replacedById) => archiveWorkflow(w.id, replacedById) : undefined}
                  onUnarchive={w.workflowStatus === "deprecated" ? () => unarchiveWorkflow(w.id) : undefined}
                  onRename={renameWorkflow}
                  renderActionsOnly
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderSectionHeading(
    label: string,
    count: number,
    sortLabel: string,
    opts?: { onAdd?: () => void }
  ) {
    return (
      <div className="flex items-center gap-2 mx-4 mt-5 mb-1">
        <h2 className="text-sm font-semibold text-slate-700">{label}</h2>
        <span className="text-xs text-slate-400">({count})</span>
        <span className="text-xs text-slate-400 italic ml-1">· {sortLabel}</span>
        <div className="flex-1" />
        {opts?.onAdd && (
          <button
            type="button"
            onClick={opts.onAdd}
            className="text-xs text-[var(--color-primary)] hover:opacity-80"
          >
            + Add workflow
          </button>
        )}
      </div>
    );
  }

  function renderSections() {
    const items = visibleWorkflows;
    if (items.length === 0) return renderEmpty();

    if (groupBy === "flat") {
      const sectionUsesPriority = items.some((w) => isPriorityRelevant(w.triggerType));
      const sorted = sortSection(items, sectionUsesPriority);
      return (
        <div className="bg-white border rounded-lg border-slate-200 mx-4 mt-3">
          {renderSectionTable(sorted, { showPriorityColumn: sectionUsesPriority, keyPrefix: "flat" })}
        </div>
      );
    }

    // Build section groups.
    const groups = new Map<string, WorkflowListItem[]>();
    if (groupBy === "trigger") {
      // Preserve triggerDefs declaration order so sections read top-to-bottom
      // from pre-conversation -> conversation -> post -> integrations.
      const order = Object.keys(triggerDefs);
      order.forEach((t) => {
        const group = items.filter((w) => w.triggerType === t);
        if (group.length > 0) groups.set(t, group);
      });
      // Any trigger not in triggerDefs (shouldn't happen, but defensive)
      items.forEach((w) => {
        if (!order.includes(w.triggerType)) {
          const existing = groups.get(w.triggerType) ?? [];
          existing.push(w);
          groups.set(w.triggerType, existing);
        }
      });
    } else {
      // groupBy === "category"
      items.forEach((w) => {
        const cat = (w.category && w.category.trim()) || "Uncategorized";
        const existing = groups.get(cat) ?? [];
        existing.push(w);
        groups.set(cat, existing);
      });
    }

    const entries = Array.from(groups.entries());
    if (groupBy === "category") {
      entries.sort(([a], [b]) => {
        if (a === "Uncategorized") return 1;
        if (b === "Uncategorized") return -1;
        return a.localeCompare(b);
      });
    }

    return (
      <>
        {entries.map(([key, group]) => {
          const sectionUsesPriority = group.some((w) => isPriorityRelevant(w.triggerType));
          const sorted = sortSection(group, sectionUsesPriority);
          const label = groupBy === "trigger"
            ? (triggerDefs[key as Trigger] ?? key)
            : key;
          const sortLabel = sectionUsesPriority ? "Sorted by priority" : "Sorted A–Z";
          return (
            <div key={key}>
              {renderSectionHeading(label, group.length, sortLabel, {
                onAdd: () => navigate("/workflows/create"),
              })}
              <div className="bg-white border rounded-lg border-slate-200 mx-4">
                {renderSectionTable(sorted, { showPriorityColumn: sectionUsesPriority, keyPrefix: key })}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  function renderReusableFlows() {
    if (templates.length === 0) return null;
    return (
      <div className="mx-4 mt-4 mb-2">
        <button
          type="button"
          onClick={() => setReusableOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-900 transition-colors"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          Reusable Flows
          <span className="ml-1 text-xs font-normal text-slate-500">({templates.length})</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`transition-transform ${reusableOpen ? "rotate-180" : ""}`}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {reusableOpen && (
          <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
            <div className="px-4 py-2 text-xs text-slate-500">
              Call these in any workflow using a <strong>Call Workflow</strong> node — great for shared greeting scripts, data lookups, and escalation flows.
            </div>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-violet-200 h-9 text-xs text-slate-500 font-semibold">
                  <th className="pl-4 w-1/3">Name</th>
                  <th className="w-1/4">Trigger</th>
                  <th className="w-24">Status</th>
                  <th className="w-20 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, i) => (
                  <tr key={t.id} className={`border-b border-violet-100 hover:bg-violet-100/40 ${i === templates.length - 1 ? "border-b-0" : ""}`}>
                    <td className="pl-4 py-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/workflows/${t.id}`)}
                        className="text-sm font-medium text-slate-800 hover:text-violet-700 transition-colors"
                      >
                        {t.name}
                      </button>
                    </td>
                    <td className="py-2"><TriggerBadge trigger={t.triggerType} /></td>
                    <td className="py-2">
                      <WorkflowItem
                        item={t}
                        isLast={i === templates.length - 1}
                        toggle={() => toggleWorkflow(t.id)}
                        remove={() => removeWorkflow(t.id)}
                        duplicate={() => duplicateWorkflow(t.id)}
                        onRename={renameWorkflow}
                        renderStatusToggleOnly
                      />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <WorkflowItem
                        item={t}
                        isLast={i === templates.length - 1}
                        toggle={() => toggleWorkflow(t.id)}
                        remove={() => removeWorkflow(t.id)}
                        duplicate={() => duplicateWorkflow(t.id)}
                        onRename={renameWorkflow}
                        renderActionsOnly
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  function renderBulkBar() {
    if (selectedIds.size === 0) return null;
    return (
      <div className="mx-4 mt-2">
        <BulkActionBar>
          <span className="font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <button type="button" onClick={bulkEnable} className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium">Enable</button>
          <button type="button" onClick={bulkDisable} className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium">Disable</button>
          <button type="button" onClick={bulkArchive} className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium">Archive</button>
          <button type="button" onClick={() => setSelectedIds(new Set())} className="px-2 py-1 rounded border border-transparent hover:bg-slate-100 text-slate-500 text-xs">Clear</button>
        </BulkActionBar>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {renderTopbar()}
      {viewMode === "journey" ? (
        <WorkflowsJourneyView />
      ) : (
        <>
          {renderPriorityBanner()}
          {renderFilterRow()}
          {renderBulkBar()}
          {renderSections()}
          {renderReusableFlows()}
        </>
      )}
    </div>
  );
}
